import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Send } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { pathname } = useLocation();
  const showCta = !["/", "/services", "/about"].includes(pathname);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      toast.success("TRANSMISSION RECEIVED — SIGNAL LOCKED");
      setEmail("");
    } catch (err) {
      toast.error("Invalid email. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      className="relative z-10 border-t border-[#1E2BFF]/25 mt-32"
      data-testid="site-footer"
    >
      {/* CTA band */}
      {showCta && (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 border-b border-[#1E2BFF]/20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start lg:items-end">
            <div className="lg:col-span-8">
              <div className="overline mb-4">// TRANSMIT.001</div>
              <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white leading-[1.05]">
                Ready to architect
                <br />
                <span className="glow-cyan text-[#00F0FF]">what comes next?</span>
              </h3>
              <p className="mt-5 text-[#9BA1D6] text-base max-w-xl leading-relaxed">
                Subscribe for dispatches from the lab — agent research notes, IoT
                case teardowns, and architecture deep-dives. No noise.
              </p>
            </div>
            <div className="lg:col-span-4">
              <form
                onSubmit={subscribe}
                className="flex items-stretch gap-0 border border-[#1E2BFF]/40 hud-card"
                data-testid="newsletter-form"
              >
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="OPERATOR@DOMAIN.IO"
                  className="hud-input border-0 bg-transparent"
                  data-testid="newsletter-email-input"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="px-5 bg-[#1E2BFF] hover:bg-[#00F0FF] hover:text-[#05060F] text-[#fff] transition-all duration-200 flex items-center justify-center min-w-[56px]"
                  data-testid="newsletter-submit-btn"
                  aria-label="Subscribe"
                >
                  <Send size={16} />
                </button>
              </form>
              <p className="font-mono text-[10px] tracking-[0.25em] text-[#9BA1D6]/70 uppercase mt-3">
                {loading ? "// transmitting..." : "// zero spam / encrypted channel"}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main footer grid */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10">
        <div className="col-span-2">
          <BrandLogo className="h-14 sm:h-16 max-w-[240px]" />
          <p className="mt-5 text-[#9BA1D6] text-sm leading-relaxed max-w-sm">
            A research-driven engineering studio building agentic AI, IoT
            platforms, and transformation systems for operators of the
            post-cloud era.
          </p>
          <div className="mt-6 flex items-center gap-3">
            <span className="pulse-dot" />
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#9BA1D6]">
              SYSTEMS ONLINE // IND-01
            </span>
          </div>
        </div>

        <FooterCol
          title="Capabilities"
          links={[
            { label: "Application Development", to: "/services" },
            { label: "Digital Transformation", to: "/services" },
            { label: "Agentic AI", to: "/services" },
            { label: "Internet of Things", to: "/services" },
          ]}
        />
        <FooterCol
          title="Company"
          links={[
            { label: "About", to: "/about" },
            { label: "Work", to: "/case-studies" },
            { label: "Careers", to: "/careers" },
            { label: "Contact", to: "/contact" },
          ]}
        />
        <div>
          <h4 className="font-mono text-[11px] tracking-[0.28em] uppercase text-[#00F0FF] mb-4">
            Coordinates
          </h4>
          <ul className="space-y-3 text-sm text-[#9BA1D6]">
            <li>contact@avinyaresearch.in</li>
            <li>+91 7019199645</li>
            <li>Remote First - Pan India</li>
          </ul>
          <Link
            to="/contact"
            className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-white link-underline"
            data-testid="footer-contact-link"
          >
            Engage Lab <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      <div className="border-t border-[#1E2BFF]/20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-4 font-mono text-[10px] tracking-[0.25em] uppercase text-[#9BA1D6]/80">
          <div>© {new Date().getFullYear()} Avinya Research Labs // All Rights Reserved</div>
          <div className="flex gap-6">
            <span>v.2.5.1</span>
            <span>Privacy</span>
            <span>Terms</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <h4 className="font-mono text-[11px] tracking-[0.28em] uppercase text-[#00F0FF] mb-4">
        {title}
      </h4>
      <ul className="space-y-3">
        {links.map((l) => (
          <li key={l.label}>
            <Link
              to={l.to}
              className="text-sm text-white/80 hover:text-[#00F0FF] transition-colors link-underline"
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
